/**
 * Local-dev one-click login.
 *
 * Persists the session returned by the backend `auth.devLogin` endpoint so the
 * tRPC client can attach `Authorization: Bearer dev_token_<id>` (recognized in
 * aixinternal-api `lib/trpc.ts`) and the auth context can hydrate the signed-in
 * user without a Google OAuth round-trip.
 *
 * SECURITY: the whole feature is gated behind Vite's `DEV` flag, so it is tree-
 * shaken out of the production build. The deployed Command Center stays Google-
 * only — there is no credential-less bypass on prod. The backend `devLogin`
 * endpoint is independently hard-gated off when NODE_ENV=production.
 */

const DEV_SESSION_KEY = "aix-dev-session"

/** True only under `vite dev` — never in a production build. */
export const isDevLoginEnabled = import.meta.env.DEV && import.meta.env.VITE_GATEWAY_AUTHORITY_MODE !== "true"

export interface DevSession {
  user: {
    id: string
    email: string
    user_metadata?: { name?: string; avatar_url?: string | null }
  }
  /** "dev_token_<userId>" — sent as the Bearer token the backend recognizes. */
  accessToken: string
  /** Unix seconds. */
  expiresAt: number
}

export function getDevSession(): DevSession | null {
  if (!isDevLoginEnabled) return null
  try {
    const raw = localStorage.getItem(DEV_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DevSession
    // Require a finite, positive expiry. A missing/0/NaN value must be treated as
    // invalid (not "never expires"), else a corrupted persisted session lingers.
    const expiresAtValid =
      typeof parsed.expiresAt === "number" && Number.isFinite(parsed.expiresAt) && parsed.expiresAt > 0
    const expired = !expiresAtValid || parsed.expiresAt * 1000 < Date.now()
    if (!parsed.accessToken || expired) {
      localStorage.removeItem(DEV_SESSION_KEY)
      return null
    }
    return parsed
  } catch {
    localStorage.removeItem(DEV_SESSION_KEY)
    return null
  }
}

export function setDevSession(session: DevSession): void {
  localStorage.setItem(DEV_SESSION_KEY, JSON.stringify(session))
}

export function clearDevSession(): void {
  localStorage.removeItem(DEV_SESSION_KEY)
}
